import { ROUTER_PATH } from "../config/router";

/** 推送点击 payload（与 App 端 postMessage 一致：title、body、type 及自定义字段） */
export interface PushNotificationClickedPayload {
  type?: string;
  title?: string;
  body?: string;
  market?: string;
  category?: string;
  slug?: string;
  link?: string;
  /** V3.0.2 TRACKER：`invest` | `plan`（本期不做 digest） */
  domain?: string;
  trackerId?: string;
  /** 后端下发的记录 id；前端落地仍复用 `signal_id` query */
  recordId?: string;
  /** 兼容旧字段，优先级低于 `recordId` */
  signalId?: string;
  goalId?: string;
  recall_category?: string;
  [key: string]: unknown;
}

/** V3.0.2 Push 点击 → 站内 path（MVP） */
export const PUSH_TYPE_TRACKER = "fcm.common.title.TRACKER";

const RECALL_SILENT_TYPES = new Set([
  "fcm.title.recall.silent.7d",
  "fcm.title.recall.silent.30d",
]);

const RECALL_GOAL_TYPES = new Set([
  "fcm.title.recall.goal.3d",
  "fcm.title.recall.goal.7d",
  "fcm.title.recall.goal.30d",
]);

function buildDeskDetailPath(trackerId: string, recordId?: string): string {
  const base = `${ROUTER_PATH.DESK}/${encodeURIComponent(trackerId)}`;
  if (!recordId) {
    return base;
  }
  return `${base}?signal_id=${encodeURIComponent(recordId)}`;
}

function appendQuery(
  path: string,
  params: Record<string, string | undefined>,
): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      qs.set(key, value);
    }
  }
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

/**
 * 将 Push 点击 payload 解析为站内路由。
 * - 召回 silent → /brief；goal → /plan(?goalId=)
 * - TRACKER plan → /plan(?goalId=)
 * - TRACKER invest → /desk/{trackerId}(?signal_id={recordId})；缺 trackerId 兜底 /desk/list
 * - 本期不做 digest
 */
export function resolvePushNotificationRoute(
  data: PushNotificationClickedPayload,
): string | undefined {
  const pushType = data?.type;
  if (!pushType) {
    return undefined;
  }

  if (RECALL_SILENT_TYPES.has(pushType)) {
    return ROUTER_PATH.BRIEF;
  }

  if (
    RECALL_GOAL_TYPES.has(pushType) ||
    pushType.startsWith("fcm.title.recall.goal.")
  ) {
    return appendQuery(ROUTER_PATH.PLAN, {
      goalId: data.goalId ? String(data.goalId) : undefined,
    });
  }

  if (pushType === PUSH_TYPE_TRACKER) {
    const domain = String(data.domain ?? "");
    if (domain === "plan") {
      return appendQuery(ROUTER_PATH.PLAN, {
        goalId: data.goalId ? String(data.goalId) : undefined,
      });
    }

    const trackerId = data.trackerId ? String(data.trackerId) : "";
    if (trackerId) {
      const recordId = data.recordId
        ? String(data.recordId)
        : data.signalId
          ? String(data.signalId)
          : undefined;
      return buildDeskDetailPath(trackerId, recordId);
    }
    return ROUTER_PATH.DESK_LIST;
  }

  return undefined;
}
